// =========================================================================
// GlobalSearch
//
// A universal, cross-org / cross-store command bar — opened from a single
// search icon and rendered as a centered, Spotlight-style floating overlay.
// It calls `globalSearch` (backed by the SECURITY INVOKER RPC
// `core.rpc_global_search`), so results are EXACTLY the rows the signed-in
// user can already read via RLS — every store, every org they have access to,
// and nothing more.
//
// Picking a result switches the active workspace (org + store + mode) to the
// result's tenant — surfacing a toast so the operator knows they've moved —
// then navigates to its detail page. This is necessary because detail pages
// render inside the active context; a result may live in a different
// store/org than the one currently open.
// =========================================================================

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@pc/ui';
import {
  globalSearch,
  listStoresForOrg,
  type GlobalSearchEntityType,
  type GlobalSearchResult,
} from '@pc/db';
import { useActiveContext } from '../auth/ActiveContextProvider';

// Fixed display order for the grouped result list.
const TYPE_ORDER: GlobalSearchEntityType[] = [
  'client',
  'order',
  'shipment',
  'product',
  'lot',
  'wholesaler',
  'vendor',
  'contract',
  'appointment',
];

function pathForResult(r: GlobalSearchResult): string {
  switch (r.entityType) {
    case 'client':
      return `/clients/${r.entityId}`;
    case 'order':
      return `/orders/${r.entityId}`;
    case 'shipment':
      return `/fulfillment/${r.entityId}`;
    case 'product':
      return `/products/${r.entityId}`;
    case 'lot':
      return `/lots/${r.entityId}`;
    case 'wholesaler':
      return `/wholesalers/${r.entityId}`;
    case 'vendor':
      return `/vendors/${r.entityId}`;
    case 'contract':
      return `/contracts/${r.entityId}`;
    case 'appointment':
      return `/calendar/${r.entityId}`;
    default:
      return '/';
  }
}

// Recently opened results, persisted so the operator can jump back to a record
// they just visited without retyping. Capped + deduped by entity. Stored under a
// single key shared by both mounted triggers (desktop bar + mobile header).
const RECENT_KEY = 'pc.globalSearch.recent';
const RECENT_MAX = 8;

function loadRecent(): GlobalSearchResult[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as GlobalSearchResult[]) : [];
  } catch {
    return [];
  }
}

function saveRecent(items: GlobalSearchResult[]): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(items.slice(0, RECENT_MAX)));
  } catch {
    /* ignore quota / privacy-mode failures */
  }
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="9" cy="9" r="6" />
      <path d="m17 17-3.5-3.5" />
    </svg>
  );
}

export function GlobalSearch({ enableShortcut = true }: { enableShortcut?: boolean } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { activeOrgId, activeStoreId, setActiveOrg, setActiveStore } = useActiveContext();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [navigating, setNavigating] = useState(false);
  const [recent, setRecent] = useState<GlobalSearchResult[]>([]);
  const listboxId = useId();

  // Debounce the query feeding react-query so we don't fan out 8 ILIKE scans
  // on every keystroke.
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query.trim()), 200);
    return () => window.clearTimeout(id);
  }, [query]);

  const enabled = debounced.length >= 2;
  const { data, isFetching } = useQuery({
    queryKey: ['globalSearch', debounced],
    queryFn: () => globalSearch(debounced, 6),
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const results = useMemo(() => data ?? [], [data]);

  // Stable flat order (grouped by type) for keyboard navigation + rendering.
  const ordered = useMemo(() => {
    const byType = new Map<GlobalSearchEntityType, GlobalSearchResult[]>();
    for (const r of results) {
      const arr = byType.get(r.entityType) ?? [];
      arr.push(r);
      byType.set(r.entityType, arr);
    }
    const flat: GlobalSearchResult[] = [];
    for (const type of TYPE_ORDER) {
      for (const r of byType.get(type) ?? []) flat.push(r);
    }
    return flat;
  }, [results]);

  // When the box is empty we surface recent picks; once a query is active we
  // show live results. Both feed the same keyboard model + row renderer.
  const showRecent = !enabled && recent.length > 0;
  const display = enabled ? ordered : showRecent ? recent : [];

  // Cmd/Ctrl+K -> open the overlay (toggles closed if already open). Only the
  // instance that owns the shortcut registers the listener, so mounting the
  // trigger in both the desktop bar and the mobile header doesn't double-fire.
  useEffect(() => {
    if (!enableShortcut) return;
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enableShortcut]);

  // Autofocus the field whenever the overlay opens, and refresh the recent list
  // from storage (the sibling trigger instance may have updated it).
  useEffect(() => {
    if (!open) return;
    setRecent(loadRecent());
    setActiveIndex(0);
    const id = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 20);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [debounced]);

  function close() {
    setOpen(false);
    setQuery('');
    setDebounced('');
  }

  function rememberRecent(r: GlobalSearchResult) {
    setRecent((prev) => {
      const next = [
        r,
        ...prev.filter(
          (p) => !(p.entityType === r.entityType && p.entityId === r.entityId),
        ),
      ].slice(0, RECENT_MAX);
      saveRecent(next);
      return next;
    });
  }

  function clearRecent() {
    setRecent([]);
    saveRecent([]);
  }

  async function pick(r: GlobalSearchResult) {
    setNavigating(true);
    rememberRecent(r);
    try {
      const orgChanged = r.orgId !== activeOrgId;
      if (orgChanged) await setActiveOrg(r.orgId);

      // Detail pages render inside store mode and need an active store. Use the
      // result's own store when it has one; for org-scoped catalog entities
      // (product/lot) fall back to the current store (if same org) or the first
      // store the user can see in the target org.
      let storeId = r.storeId;
      if (!storeId) {
        if (!orgChanged && activeStoreId) {
          storeId = activeStoreId;
        } else {
          const stores = await listStoresForOrg(r.orgId);
          storeId = stores[0]?.id ?? null;
        }
      }
      const storeChanged = Boolean(storeId) && storeId !== activeStoreId;
      if (storeId) setActiveStore(storeId);

      // Tell the operator they've moved to a different workspace.
      if (orgChanged && r.orgName && r.storeName) {
        toast.push(
          t('globalSearch.switchedBoth', {
            store: r.storeName,
            org: r.orgName,
            defaultValue: 'Now viewing {{store}} · {{org}}',
          }),
          'info',
        );
      } else if (orgChanged && r.orgName) {
        toast.push(
          t('globalSearch.switchedOrg', {
            org: r.orgName,
            defaultValue: 'Switched to {{org}}',
          }),
          'info',
        );
      } else if (storeChanged && r.storeName) {
        toast.push(
          t('globalSearch.switchedStore', {
            store: r.storeName,
            defaultValue: 'Now viewing {{store}}',
          }),
          'info',
        );
      }

      navigate(pathForResult(r));
    } finally {
      setNavigating(false);
      close();
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (!display.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(display.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = display[activeIndex];
      if (r) void pick(r);
    }
  }

  const groupLabel: Record<GlobalSearchEntityType, string> = {
    client: t('globalSearch.group.client', 'Clients'),
    order: t('globalSearch.group.order', 'Orders'),
    shipment: t('globalSearch.group.shipment', 'Shipments'),
    product: t('globalSearch.group.product', 'Products'),
    lot: t('globalSearch.group.lot', 'Lots'),
    wholesaler: t('globalSearch.group.wholesaler', 'Wholesalers'),
    vendor: t('globalSearch.group.vendor', 'Vendors'),
    contract: t('globalSearch.group.contract', 'Contracts'),
    appointment: t('globalSearch.group.appointment', 'Appointments'),
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('globalSearch.openLabel', 'Open search')}
        title={t('globalSearch.openLabel', 'Open search')}
        className="inline-flex items-center gap-2 border border-rule px-2.5 py-1.5 text-muted transition-colors hover:border-rule-strong hover:text-ink"
      >
        <SearchIcon className="h-4 w-4" />
        <kbd
          aria-hidden
          className="hidden items-center px-1.5 py-0.5 text-[10px] tabular border border-rule text-muted bg-paper sm:inline-flex"
        >
          {'\u2318K'}
        </kbd>
      </button>

      {open
        ? createPortal(
            <div
              role="dialog"
              aria-modal="true"
              aria-label={t('globalSearch.ariaLabel', 'Search all stores and organizations you can access')}
              className="fixed inset-0 z-50"
            >
              <div
                className="absolute inset-0 bg-ink/30 backdrop-blur-sm"
                onClick={close}
              />
              <div className="absolute left-1/2 top-[12vh] w-[92vw] max-w-2xl -translate-x-1/2">
                <div className="border border-rule-strong bg-paper shadow-card">
                  <div className="flex items-center gap-2 border-b border-rule px-3">
                    <SearchIcon className="h-4 w-4 shrink-0 text-muted" />
                    <input
                      ref={inputRef}
                      type="search"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={onKeyDown}
                      placeholder={t('globalSearch.placeholder', 'Search everything…')}
                      aria-label={t(
                        'globalSearch.ariaLabel',
                        'Search all stores and organizations you can access',
                      )}
                      aria-controls={display.length ? listboxId : undefined}
                      aria-autocomplete="list"
                      autoComplete="off"
                      className="min-w-0 flex-1 bg-transparent py-3 text-sm text-ink outline-none placeholder:text-muted-2"
                    />
                    <kbd
                      aria-hidden
                      className="shrink-0 px-1.5 py-0.5 text-[10px] tabular border border-rule text-muted"
                    >
                      {'esc'}
                    </kbd>
                  </div>

                  {!enabled && !showRecent ? (
                    <div className="px-3 py-6 text-xs text-muted">
                      {t(
                        'globalSearch.hint',
                        'Search clients, orders, products, and more across every workspace you can access.',
                      )}
                    </div>
                  ) : enabled && ordered.length === 0 ? (
                    <div className="px-3 py-6 text-xs text-muted">
                      {isFetching
                        ? t('globalSearch.searching', 'Searching…')
                        : t('globalSearch.empty', 'No matches.')}
                    </div>
                  ) : (
                    <ul
                      id={listboxId}
                      role="listbox"
                      className="max-h-[60vh] overflow-auto"
                    >
                      {showRecent ? (
                        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-rule bg-paper-2 px-3 py-1 text-[10px] uppercase tracking-wide text-muted">
                          <span>{t('globalSearch.recent', 'Recent')}</span>
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              clearRecent();
                            }}
                            className="normal-case tracking-normal text-muted underline-offset-2 hover:text-ink hover:underline"
                          >
                            {t('globalSearch.clearRecent', 'Clear')}
                          </button>
                        </div>
                      ) : null}
                      {display.map((r, i) => {
                        const prev = display[i - 1];
                        const showHeader =
                          !showRecent && (!prev || prev.entityType !== r.entityType);
                        const workspace = [r.orgName, r.storeName]
                          .filter(Boolean)
                          .join(' · ');
                        return (
                          <li key={`${r.entityType}:${r.entityId}`}>
                            {showHeader ? (
                              <div className="sticky top-0 border-b border-rule bg-paper-2 px-3 py-1 text-[10px] uppercase tracking-wide text-muted">
                                {groupLabel[r.entityType]}
                              </div>
                            ) : null}
                            <button
                              type="button"
                              role="option"
                              aria-selected={i === activeIndex}
                              disabled={navigating}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                void pick(r);
                              }}
                              onMouseEnter={() => setActiveIndex(i)}
                              className={`flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left ${
                                i === activeIndex ? 'bg-linen' : 'hover:bg-paper-2'
                              }`}
                            >
                              <span className="flex min-w-0 flex-col">
                                <span className="truncate text-sm text-ink">{r.title}</span>
                                {r.subtitle ? (
                                  <span className="truncate text-xs text-muted">
                                    {r.subtitle}
                                  </span>
                                ) : null}
                              </span>
                              {workspace ? (
                                <span className="max-w-[45%] shrink-0 truncate text-[10px] tabular uppercase tracking-wide text-muted">
                                  {workspace}
                                </span>
                              ) : null}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
